/**
 * Helpers to build denormalized wishlist item payloads from Sharetribe listings.
 * Snapshots avoid extra Marketplace API calls when rendering the wishlist page.
 */

import { types } from '../../util/sdkLoader';

const { Money } = types;

const pickImageUrl = listing => {
  const firstImage = listing?.images?.[0];
  const variants = firstImage?.attributes?.variants || {};
  const preferredKeys = ['listing-card', 'scaled-small', 'landscape-crop', 'default'];
  for (const key of preferredKeys) {
    const variant = variants[key];
    if (variant?.url) {
      return variant.url;
    }
  }
  const firstVariant = Object.values(variants)[0];
  return firstVariant?.url || null;
};

/**
 * @param {Object} listing Sharetribe listing entity
 * @param {string} userId Sharetribe currentUser.id.uuid
 * @returns {Object} Firestore document fields
 */
export const buildWishlistItemFromListing = (listing, userId) => {
  const listingId = listing?.id?.uuid;
  if (!listingId || !userId) {
    throw new Error('buildWishlistItemFromListing requires listing.id and userId');
  }

  const { title = '', description = '', publicData = {}, price } =
    listing?.attributes || {};
  const authorId = listing?.author?.id?.uuid || listing?.relationships?.author?.data?.id?.uuid;

  return {
    listingId,
    userId,
    listingTitle: title,
    listingImage: pickImageUrl(listing),
    authorId: authorId || null,
    createdAt: new Date().toISOString(),
    metadata: {
      slug: title
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_-]+/g, '-')
        .replace(/^-+|-+$/g, ''),
      descriptionSnippet: description?.slice(0, 160) || '',
      priceAmount: price?.amount ?? null,
      priceCurrency: price?.currency ?? null,
      publicData: publicData || {},
    },
  };
};

/**
 * Minimal snapshot for anonymous (pre-login) storage.
 */
export const buildAnonymousWishlistEntry = listing => {
  const listingId = listing?.id?.uuid;
  if (!listingId) {
    return null;
  }
  return buildWishlistItemFromListing(
    { ...listing, author: listing.author || { id: { uuid: null } } },
    'anonymous'
  );
};

/**
 * Reconstructs a minimal Sharetribe-shaped listing for {@link ListingCard} from a wishlist
 * snapshot (same fields as {@link buildWishlistItemFromListing}).
 *
 * @param {Object} item wishlist item from Firestore / anonymous storage
 * @param {string} [variantPrefix='listing-card'] must match `config.layout.listingImage.variantPrefix`
 * @returns {Object|null} listing entity, or null if `listingId` is missing
 */
export const buildListingCardListingFromWishlistItem = (item, variantPrefix = 'listing-card') => {
  const listingId = item?.listingId;
  if (!listingId) {
    return null;
  }

  const title = item.listingTitle || '';
  const description =
    item?.metadata?.descriptionSnippet != null ? String(item.metadata.descriptionSnippet) : '';
  const publicData =
    item?.metadata?.publicData && typeof item.metadata.publicData === 'object'
      ? item.metadata.publicData
      : {};

  const url = item.listingImage || null;
  const images = url
    ? [
        {
          id: { uuid: `wishlist-snapshot-${listingId}` },
          type: 'image',
          attributes: {
            variants: {
              [variantPrefix]: { url, width: 400 },
              [`${variantPrefix}-2x`]: { url, width: 800 },
            },
          },
        },
      ]
    : [];

  let price;
  if (item?.metadata?.priceAmount != null && item?.metadata?.priceCurrency) {
    try {
      price = new Money(item.metadata.priceAmount, item.metadata.priceCurrency);
    } catch {
      price = undefined;
    }
  }

  return {
    id: { uuid: listingId },
    type: 'listing',
    attributes: {
      title,
      description,
      ...(price ? { price } : {}),
      publicData,
    },
    images,
    author: null,
  };
};
